/* BRIEF 136 §3 [RULED 2026-08-25, Laurent] — AN EVENT'S STATUS, FROM THE TWO
 * COLUMNS THAT HOLD IT.
 *
 * The ruling names four: Confirmed (shows nothing extra: just the counter, as
 * today) · Unconfirmed (a circle-question icon INSTEAD of the counter, in the
 * counter's colour) · Cancelled (a circle-off icon instead, same colour) ·
 * Archived.
 *
 * ── WHY IT IS A DERIVATION AND NOT A COLUMN READ ────────────────────────────
 *
 * Three of the four live in `AgendaEvent.status`; the fourth is the Phase-28
 * `archived` boolean, which already carries an index, an ArchiveView, a restore
 * and a purge. Writing `archived` into the status string as well would be one
 * fact in two stores — the arrangement Task carried until 112 §6, where a
 * `status:'archived'` string sat beside the boolean and produced rows "no list
 * retires and ArchiveView never sees". Undoing it cost a write-boundary
 * conversion (App.tsx:6928) and a server arm (routes/tasks.js:198-204).
 *
 * So the two columns stay as they are and ONE function answers the question
 * every reader actually has. It is `subtaskStatusOf`'s shape
 * (src/lib/subtaskHelpers.ts): resolve a display rung from the column when it
 * has one and from the boolean when it does not.
 *
 * ── NULL IS `confirmed`, AND THAT IS §3's PROOF ─────────────────────────────
 *
 * §3: "DEFAULT: EXISTING EVENTS ARE CONFIRMED. Nothing in the agenda may change
 * appearance for an event nobody has touched. That is this §'s proof."
 *
 * The column is nullable with no default and nothing was backfilled, so every
 * event that existed before this brief reads `null` here. `null` resolves to
 * `confirmed`, and `confirmed` is DEFINED as "declares no icon, so the host
 * draws the counter it always drew". The proof is structural rather than
 * empirical: there is no state in which an untouched event differs, because
 * nothing about an untouched event changed.
 */
import { getEventStatusMeta } from "./taxonomyHelpers";

/** What a NULL column means. */
export const EVENT_STATUS_DEFAULT = "confirmed";

/** The one code the column never stores — the Phase-28 boolean holds it. */
export const EVENT_STATUS_ARCHIVED = "archived";

export type EventStatusRow = {
  status?: string | null;
  archived?: boolean | null;
} | null | undefined;

/**
 * An event's status FOR DISPLAY, over all four codes.
 *
 * The boolean wins, and the order is the point: an archived event is archived
 * whatever its status column says, exactly as ArchiveView, `where.archived` and
 * the agenda's own filters already treat it.
 */
export function eventStatusOf(ev: EventStatusRow): string {
  if (!ev) return EVENT_STATUS_DEFAULT;
  if (ev.archived) return EVENT_STATUS_ARCHIVED;
  const s = ev.status;
  return typeof s === "string" && s !== "" ? s : EVENT_STATUS_DEFAULT;
}

/**
 * The Lucide name of the glyph that REPLACES the day counter for this status,
 * or `null` when the counter stands.
 *
 * Read from the firm's own taxonomy row (`meta.icon`) through
 * `getEventStatusMeta`, so a firm that adds a fifth status can give it a glyph
 * — and one that clears a cell gets the counter back, which is why the empty
 * string is honoured rather than treated as unset.
 */
export function eventStatusIcon(
  code: string,
  entries?: unknown,
): string | null {
  const icon = getEventStatusMeta(code, entries).icon;
  return typeof icon === "string" && icon !== "" ? icon : null;
}

/**
 * The codes a user may CHOOSE, in the firm's own taxonomy order.
 *
 * `archived` is excluded for the reason it is excluded from §1's task pill:
 * archiving is the archive icon's job, and on events that icon is ALWAYS
 * visible (§3(4), deliberately unlike §2's rule for tasks). A status field
 * that could also set it would be a second way to reach one state.
 *
 * THE FALLBACK IS THE BUILT-IN LADDER, never an empty list: a menu that empties
 * itself while the taxonomy is still loading is a control that flickers into
 * uselessness. Mirrors `pickableStatusCodes` (src/lib/taskCounters.ts).
 */
export function pickableEventStatusCodes(entries?: unknown): string[] {
  const rows = Array.isArray(entries) ? entries : [];
  const codes = rows
    .map((r) =>
      r && typeof r === "object" && !Array.isArray(r)
        ? (r as { code?: unknown }).code
        : null,
    )
    .filter((c): c is string => typeof c === "string" && c !== "");
  const ladder = codes.length
    ? codes
    : [EVENT_STATUS_DEFAULT, "unconfirmed", "cancelled", EVENT_STATUS_ARCHIVED];
  return ladder.filter((c) => c !== EVENT_STATUS_ARCHIVED);
}
